import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable, type FilterSpec } from "@/components/data";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { countryName, FLAG_LABEL, humanDate, messageFor, money } from "./format";
import { componentLabel, costOrigin, costsCsv, costsLogQuery, costSourceLabel, csvName, ENTRY_LABEL, ENTRY_TONE, exportCostsLog, filterState, flagFilterLabel, isoDay, LOGS_COPY, LOGS_PAGE_SIZE, marketLabel, paramsFrom, placeLabel, searchForFulfilment, type CostLogRow, type FulfilmentView } from "./logs";
import { LogsError, LogsToolbar } from "./LogsToolbar";

const COUNT = new Intl.NumberFormat("en-GB");
const C = LOGS_COPY.costs;
const VIEWS: FulfilmentView[] = ["current", "changes"];

/** The Costs pane: one server page of the Costs Log (cost entries plus the fulfilment standards as they stand, or every change
 * to them), newest effective date first. */
export function CostsLogPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const params = paramsFrom(search);
  const page = useQuery(costsLogQuery(params));
  const heading = (
    <div className="flex flex-col gap-2">
      <div><h2 className="text-lg font-semibold text-ks-ink">{C.title}</h2><p className="text-sm text-ks-grey-1">{C.note}</p></div>
      <Alert role="note"><AlertDescription>{C.banner}</AlertDescription></Alert>
    </div>
  );
  if (page.isPending) return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card></div>;
  if (page.error) return <div className="flex flex-col gap-3">{heading}<LogsError message={messageFor(page.error, areaName)} onRetry={() => void page.refetch()} /></div>;
  const d = page.data;
  const changes = params.fulfilment === "changes";
  const filters = filterState(params, [params.entryType, params.flag]);
  if (d.page.total === 0 && filters === "none") {
    return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{C.emptyTitle}</p><p className="text-ks-grey-1">{C.emptyBody}</p></CardContent></Card></div>;
  }
  const paneFilters: FilterSpec[] = [
    { name: "type", label: C.filters.type, kind: "choice", anyLabel: "All", options: d.facets.entry_types.map((t) => ({ value: t, label: ENTRY_LABEL[t] })) },
    { name: "flag", label: C.filters.flag, kind: "choice", anyLabel: "All", options: d.facets.flags.map((f) => ({ value: f, label: flagFilterLabel(f) })) },
  ];
  const columns: ColumnDef<CostLogRow, unknown>[] = [
    { id: "date", header: changes ? C.dated : C.columns[0], cell: ({ row: r }) => <DateCell row={r.original} changes={changes} /> },
    { id: "sku", header: C.columns[1], cell: ({ row: r }) => <div><span className="font-semibold text-ks-ink">{r.original.product} {r.original.variant}</span><div className="text-xs text-ks-grey-1">{r.original.xero_sku}</div></div> },
    { id: "market", header: C.columns[2], cell: ({ row: r }) => marketLabel(r.original.market) },
    { id: "entry", header: C.columns[3], cell: ({ row: r }) => <EntryCell row={r.original} /> },
    { id: "value", header: C.columns[4], meta: { align: "right" }, cell: ({ row: r }) => <ValueCell row={r.original} changes={changes} /> },
    { id: "source", header: C.columns[5], cell: ({ row: r }) => <SourceCell row={r.original} /> },
  ];
  const exportCsv = async () => ({ text: costsCsv((await exportCostsLog(params)).rows), name: csvName("costs", isoDay(new Date()), changes) });
  return (
    <div className="flex flex-col gap-3">
      {heading}
      <LogsToolbar areaName={areaName} pane="costs" search={search} go={go} params={params} facets={d.facets} paneFilters={paneFilters} applied={d.filters}
        total={d.page.total} trailing={C.trailing(COUNT.format(d.page.total))} exportCsv={exportCsv}>
        <div role="group" aria-label={C.fulfilment.label} className="flex gap-1">
          {VIEWS.map((v) => (
            <Button key={v} size="sm" variant={v === params.fulfilment ? "default" : "outline"} aria-pressed={v === params.fulfilment} data-nonmutating
              onClick={() => go(searchForFulfilment(search, v))}>
              {C.fulfilment[v]}
            </Button>
          ))}
        </div>
      </LogsToolbar>
      <DataTable columns={columns} rows={d.rows} page={{ pageIndex: params.page, pageSize: LOGS_PAGE_SIZE, rowCount: d.page.total }}
        onPageChange={(next) => go({ ...search, pp_lpage: next || undefined })} caption={C.title} emptyMessage={filters === "sku" ? LOGS_COPY.noHistorySku : C.noMatch} density="compact" />
      <p className="text-xs text-ks-grey-1">{C.footnote}</p>
    </div>
  );
}

function EntryCell({ row }: { row: CostLogRow }) {
  const isFulfilment = row.entry_type === "fulfilment";
  return (
    <div>
      <Badge variant="outline" className={ENTRY_TONE[row.entry_type]}>{isFulfilment ? componentLabel(row.component) : ENTRY_LABEL[row.entry_type]}</Badge>
      {isFulfilment && <div className="mt-1 text-xs text-ks-grey-1">{C.place(placeLabel(row), row.country ? countryName(row.country) : null)}</div>}
    </div>
  );
}
/** On Every change a fulfilment change is dated by the day PFC fed it (C2 dates, filters and orders it so), with its effective
 * date, PFC's evidence date, under it where that differs; every other row reads its effective date. */
function DateCell({ row, changes }: { row: CostLogRow; changes: boolean }) {
  const fed = changes && row.entry_type === "fulfilment" && row.fed_on ? row.fed_on : null;
  return (
    <>
      <span className="whitespace-nowrap text-sm">{humanDate(fed ?? row.effective_date)}</span>
      {fed && fed !== row.effective_date && <div className="whitespace-nowrap text-xs text-ks-grey-1">{C.effective(humanDate(row.effective_date))}</div>}
    </>
  );
}
function ValueCell({ row, changes }: { row: CostLogRow; changes: boolean }) {
  const flag = FLAG_LABEL[row.flag];
  const before = changes && row.entry_type === "fulfilment" ? (row.previous_value != null ? C.was(money(row.previous_value, row.currency)) : C.firstFed) : null;
  return (
    <>
      <span className="font-semibold tabular-nums text-ks-ink">{money(row.value, row.currency)}</span>
      {before && <div className="text-xs tabular-nums text-ks-grey-1">{before}</div>}
      {flag && <div><Badge variant="outline" className="border-ks-warn text-ks-warn" title={flag.title}>{flag.text}</Badge></div>}
    </>
  );
}
function SourceCell({ row }: { row: CostLogRow }) {
  const origin = costOrigin(row);
  return (
    <div>
      <span className="text-sm">{costSourceLabel(row.source)}</span>
      {origin && <div className="text-xs text-ks-grey-1">{origin}</div>}
    </div>
  );
}
